import React, { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button,
  FormGroup,
  FormControlLabel,
  Switch,
  Typography,
  MenuItem,
} from '@mui/material';

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import dayjs from 'dayjs';

import { fetchCompanies } from '../../api/companyApi';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  StyledCompanyDropDown,
  StyledSelect,
} from '../../styles/CustomersStyle/CustomerManagementStyle';
import { useTranslation } from 'react-i18next';
import { createClient, fetchClient, updateClient } from '../../api/clientApi';
import { Formik, Form, Field, ErrorMessage, useFormikContext } from 'formik';
import * as Yup from 'yup';
import TranslatedErrorMessage from '../validation/TranslatedErrorMessage';

// Reusable ErrorText component
const ErrorText = ({ children }) => (
  <Typography style={{ color: 'red', fontSize: '0.875rem' }}>
    {children}
  </Typography>
);

function CustomerFormDialog({ dialogData, handleClose }) {
  const { open, action, id } = dialogData;
  const queryClient = useQueryClient();
  const [active, setActive] = useState(false);

  const [selectedCompany, setSelectedCompany] = useState(0);
  const [fieldErrors, setFieldErrors] = useState({});
  const [clientData, setClientData] = useState({});

  const { t } = useTranslation('client');
  const text = {
    new: {
      title: 'הוספת רכב חדש',
      submit: 'הוסף רכב',
    },
    edit: {
      title: 'עדכן רכב - ',
      submit: 'עדכן',
    },
  };

  const {
    data: company,
    error: companyError,
    status: companyStatus,
  } = useQuery({
    queryKey: ['companyData'],
    queryFn: async () => {
      try {
        const result = await fetchCompanies();
        return result;
      } catch (error) {
        console.log(error);
      }
    },
  });

  const {
    data: client,
    error: clientError,
    status: clientStatus,
  } = useQuery({
    queryKey: ['singleClientData', id],
    enabled: id !== null,
    queryFn: async () => {
      try {
        const result = await fetchClient(id);
        setSelectedCompany(result?.data?.company?.uuid);
        setActive(true);
        return result;
      } catch (error) {
        console.log(error);
      }
    },
  });

  const validationSchema = Yup.object().shape({
    client_name: Yup.string().required(
      t('client.form.validation.client_name_required'),
    ),
    mobile: Yup.string()
      .required(t('client.form.validation.mobile_required'))
      .matches(/^\d{10}$/, t('client.form.validation.mobile_invalid')),
    phone: Yup.string()
      .required(t('client.form.validation.phone_required'))
      .matches(/^\d{10}$/, t('client.form.validation.phone_invalid')),
    email: Yup.string()
      .required(t('client.form.validation.email_required'))
      .email(t('client.form.validation.email_invalid')),
    company_uuid: Yup.string()
      .required(t('client.form.validation.company_uuid_required'))
      .notOneOf([0], t('client.form.validation.company_uuid_invalid')),
  });

  const defaultInitialValues = {
    client_name: '',
    mobile: '',
    phone: '',
    email: '',
    active: false,
    company_uuid: 0,
  };

  const initialValues = id
    ? {
        ...defaultInitialValues,
        client_name: client?.data?.name || '',
        mobile: client?.data?.mobile || '',
        phone: client?.data?.phone || '',
        email: client?.data?.email || '',
        active: client?.data?.status == 'active' ? true : false,
        company_uuid: client?.data?.company?.uuid || null,
      }
    : defaultInitialValues;

  const dataSubmit = async (
    values,
    { setSubmitting, setErrors, setFieldError },
  ) => {
    try {
      const formJson = {
        ...values,
      };

      // Rename client_name to name
      formJson.name = formJson.client_name;
      delete formJson.client_name;

      if (formJson.active) {
        formJson.status = 'active';
      } else {
        formJson.status = 'inactive';
      }
      delete formJson.active;
      if (id) {
        updateMutation.mutate({ uuid: id, body: formJson });
      } else {
        delete formJson.active;
        delete formJson.status;
        createMutation.mutate(formJson);
      }
    } catch (error) {
      console.log(error);
      setErrors({ message: error.message });
    } finally {
      setSubmitting(false);
    }
  };

  const createMutation = useMutation({
    mutationFn: createClient,
    onSuccess: (response) => {
      toast.success(response.message, {
        onClose: () => {
          queryClient.invalidateQueries('clientData'); // Invalidate cache
          handleClose(); // Close the dialog
        },
      });
    },
    onError: ({ response }) => {
      console.log('response', response);
      toast.error(response.data.message);

      const newErrors = {};

      const errorMessages = response.data.data || [];
      if (response.status === 404) {
        setFieldErrors({ message: response.data.message });
      } else if (response.status === 401) {
        setFieldErrors({ message: response.data.message });
      } else if (response.status === 400) {
        if (errorMessages.length === 0) {
          setFieldErrors({ message: response.data.message });
        } else {
          errorMessages.forEach((error) => {
            const { message, path } = error;
            const key = path[0];
            newErrors[key] = message;
          });
          setFieldErrors(newErrors);
        }
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ uuid, body }) => updateClient(uuid, body),
    onSuccess: (response) => {
      queryClient.invalidateQueries('clientData');
      toast.success(response.message, {
        onClose: () => {
          handleClose();
        },
      });
    },
    onError: ({ response }) => {
      console.log('response', response);
      toast.error(response.data.message);
    },
  });

  return (
    <>
      <Dialog
        open={open}
        key="customerFormDialog"
        onClose={handleClose}
        disableEscapeKeyDown
        PaperProps={{ style: { width: '500px' } }}
      >
        <DialogTitle>{t('client.form.add_title')}</DialogTitle>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize
          onSubmit={dataSubmit}
        >
          {({ isSubmitting, errors, touched, values, setFieldValue }) => (
            <Form>
              <DialogContent sx={{ display: 'grid', gap: '20px' }}>
                <Field
                  as={TextField}
                  autoFocus
                  required
                  margin="dense"
                  id="client_name"
                  name="client_name"
                  label={t('client.form.client_name')}
                  type="text"
                  fullWidth
                  error={touched.client_name && !!errors.client_name}
                  helperText={<TranslatedErrorMessage name="client_name" />}
                />
                {fieldErrors.name && <ErrorText>{fieldErrors.name}</ErrorText>}

                <Field
                  as={TextField}
                  required
                  margin="dense"
                  id="mobile"
                  name="mobile"
                  label={t('client.form.mobile')}
                  fullWidth
                  error={touched.mobile && !!errors.mobile}
                  helperText={<TranslatedErrorMessage name="mobile" />}
                />
                {fieldErrors.mobile && (
                  <ErrorText>{fieldErrors.mobile}</ErrorText>
                )}

                <Field
                  as={TextField}
                  required
                  id="phone"
                  name="phone"
                  label={t('client.form.phone')}
                  fullWidth
                  error={touched.phone && !!errors.phone}
                  helperText={<TranslatedErrorMessage name="phone" />}
                />
                {fieldErrors.phone && (
                  <ErrorText>{fieldErrors.phone}</ErrorText>
                )}

                <Field
                  as={TextField}
                  required
                  id="email"
                  name="email"
                  label={t('client.form.email')}
                  fullWidth
                  error={touched.email && !!errors.email}
                  helperText={<TranslatedErrorMessage name="email" />}
                />
                {fieldErrors.email && (
                  <ErrorText>{fieldErrors.email}</ErrorText>
                )}

                <StyledCompanyDropDown>
                  <Field
                    as={StyledSelect}
                    id="company_uuid"
                    name="company_uuid"
                    value={selectedCompany}
                    onChange={(e) => {
                      setSelectedCompany(e.target.value);
                      setFieldValue('company_uuid', e.target.value);
                    }}
                  >
                    <MenuItem value={0}>{t('client.form.company')}</MenuItem>
                    {company?.data &&
                      company.data.map((item) => (
                        <MenuItem key={item.uuid} value={item.uuid}>
                          {item.name}
                        </MenuItem>
                      ))}
                  </Field>
                  <ErrorMessage name="company_uuid">
                    {(msg) => <ErrorText>{msg}</ErrorText>}
                  </ErrorMessage>
                  {fieldErrors.company_uuid && (
                    <ErrorText>{fieldErrors.company_uuid}</ErrorText>
                  )}
                </StyledCompanyDropDown>

                {id && (
                  <FormGroup>
                    <FormControlLabel
                      label={t('client.form.active')}
                      control={
                        <Field
                          as={Switch}
                          name="active"
                          checked={values.active} // Bind to Formik's state
                          onChange={(e) => {
                            setActive(e.target.checked); // Update local state
                            setFieldValue('active', e.target.checked); // Update Formik's state
                          }}
                        />
                      }
                    />
                  </FormGroup>
                )}
                {fieldErrors.active && (
                  <ErrorText>{fieldErrors.active}</ErrorText>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={handleClose}>{t('client.form.cancel')}</Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                >
                  {t('client.form.submit')}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
      <ToastContainer position="top-right" autoClose={2000} />
    </>
  );
}

export default CustomerFormDialog;
